import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SessionService } from '@core/services/use-cases/session.service';
import { UserRole } from '@core/entities/models/role.model';
import { CountryService } from '@core/services/use-cases/country.service';
import { PartnerService } from '@core/services/use-cases/partner.service';
import { BeneficiaryService } from '@core/services/use-cases/beneficiary.service';
import { Subject } from 'rxjs';

export interface BreadcrumbItem {
  label: string;
  url?: string;
  icon?: string;
  isLast?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  // Cache para evitar llamadas repetidas (almacena Promises para deduplicar llamadas concurrentes)
  private countryCache = new Map<string, Promise<string>>();
  private partnerCache = new Map<string, Promise<string>>();
  private beneficiaryCache = new Map<string, Promise<string>>();

  // Observable para notificar cuando se invalida el caché
  private cacheInvalidated$ = new Subject<void>();
  public cacheInvalidated = this.cacheInvalidated$.asObservable();

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private countryService: CountryService,
    private partnerService: PartnerService,
    private beneficiaryService: BeneficiaryService,
  ) {}

  /**
   * Genera las migajas de pan según la ruta actual y el rol del usuario
   */
  async getBreadcrumbs(): Promise<BreadcrumbItem[]> {
    const currentUser = this.sessionService.getCurrentUser();
    const url = this.router.url;
    const pathSegments = url.split('/').filter((segment) => segment);

    const breadcrumbs: BreadcrumbItem[] = [];

    if (currentUser) {
      await this.generateBreadcrumbsByRole(breadcrumbs, pathSegments, currentUser);
    } else {
      // Usuario no autenticado - migajas básicas
      this.generateBasicBreadcrumbs(breadcrumbs, pathSegments);
    }

    // Marcar el último elemento
    if (breadcrumbs.length > 0) {
      breadcrumbs[breadcrumbs.length - 1].isLast = true;
    }

    return breadcrumbs;
  }

  private async generateBreadcrumbsByRole(
    breadcrumbs: BreadcrumbItem[],
    pathSegments: string[],
    currentUser: any,
  ): Promise<void> {
    // Para ADMIN
    if (currentUser.role === UserRole.ADMIN) {
      await this.generateAdminBreadcrumbs(breadcrumbs, pathSegments);
    }
    // Para COUNTRY_ADMIN
    else if (currentUser.role === UserRole.COUNTRY_ADMIN) {
      await this.generateCountryAdminBreadcrumbs(breadcrumbs, pathSegments, currentUser);
    }
    // Para PARTNER_USER
    else if (currentUser.role === UserRole.PARTNER_USER) {
      await this.generatePartnerUserBreadcrumbs(breadcrumbs, pathSegments, currentUser);
    }
  }

  private async generateAdminBreadcrumbs(
    breadcrumbs: BreadcrumbItem[],
    pathSegments: string[],
  ): Promise<void> {
    let currentPath = '';

    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      currentPath += '/' + segment;

      switch (segment) {
        case 'admin':
          // No incluir "admin" en las migajas
          break;

        case 'countries':
          breadcrumbs.push({
            label: 'Home',
            url: '/admin/countries',
            icon: 'home',
          });
          break;

        case 'partners':
          const countryId = this.getRouteParam('countryId', pathSegments, i - 1);
          if (countryId) {
            const countryName = await this.getCountryName(countryId);
            breadcrumbs.push({
              label: `${countryName}`,
              url: `/admin/countries/${countryId}/partners`,
              icon: 'public',
            });
          }
          break;

        case 'beneficiaries':
          const partnerId = this.getRouteParam('partnerId', pathSegments, i - 1);
          if (partnerId) {
            const partnerName = await this.getPartnerName(partnerId);
            breadcrumbs.push({
              label: `${partnerName}`,
              url: currentPath,
              icon: 'business',
            });
          }
          // Check if next segment is a beneficiary ID (detail view)
          const nextSegmentAdmin = pathSegments[i + 1];
          if (nextSegmentAdmin && !['create', 'edit', 'forms'].includes(nextSegmentAdmin)) {
            const beneficiaryName = await this.getBeneficiaryName(nextSegmentAdmin);
            breadcrumbs.push({
              label: beneficiaryName,
              icon: 'person',
            });
          }
          break;

        case 'forms':
          breadcrumbs.push({
            label: 'Forms',
            url: currentPath,
            icon: 'description',
          });
          break;

        case 'create':
          this.addCreateBreadcrumb(breadcrumbs, pathSegments, i);
          break;

        case 'edit':
          this.addEditBreadcrumb(breadcrumbs, pathSegments, i);
          break;
      }
    }
  }

  private async generateCountryAdminBreadcrumbs(
    breadcrumbs: BreadcrumbItem[],
    pathSegments: string[],
    currentUser: any,
  ): Promise<void> {
    let currentPath = '';

    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      currentPath += '/' + segment;

      switch (segment) {
        case 'countries':
          // Para CountryAdmin, el country es su "Home" - obtener el nombre real usando el ID de la URL
          const urlCountryId = this.getRouteParam('countryId', pathSegments, i + 1);
          if (urlCountryId) {
            const countryName = await this.getCountryName(urlCountryId);
            breadcrumbs.push({
              label: countryName, // ✅ Obtener nombre real del país por ID
              url: `/admin/countries/${urlCountryId}/partners`, // ✅ Home es partners para country admin
              icon: 'public',
            });
          }
          break;

        case 'partners':
          // Para CountryAdmin, NO mostrar "Partners" como nivel separado
          // porque "España" ya representa su home principal que incluye partners
          break;

        case 'beneficiaries':
          const countryId = this.getRouteParam('countryId', pathSegments, i - 2);
          const partnerId = this.getRouteParam('partnerId', pathSegments, i - 1);
          if (partnerId) {
            const partnerName = await this.getPartnerName(partnerId);
            breadcrumbs.push({
              label: `${partnerName} - Beneficiaries`,
              url: `/admin/countries/${countryId}/partners/${partnerId}/beneficiaries`,
              icon: 'people',
            });
          }
          // Check if next segment is a beneficiary ID (detail view)
          const nextSegmentCountry = pathSegments[i + 1];
          if (nextSegmentCountry && !['create', 'edit', 'forms'].includes(nextSegmentCountry)) {
            const beneficiaryName = await this.getBeneficiaryName(nextSegmentCountry);
            breadcrumbs.push({
              label: beneficiaryName,
              icon: 'person',
            });
          }
          break;

        case 'forms':
          breadcrumbs.push({
            label: 'Forms',
            url: currentPath,
            icon: 'description',
          });
          break;

        case 'create':
          this.addCreateBreadcrumb(breadcrumbs, pathSegments, i);
          break;

        case 'edit':
          this.addEditBreadcrumb(breadcrumbs, pathSegments, i);
          break;
      }
    }
  }

  private async generatePartnerUserBreadcrumbs(
    breadcrumbs: BreadcrumbItem[],
    pathSegments: string[],
    currentUser: any,
  ): Promise<void> {
    let currentPath = '';

    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      currentPath += '/' + segment;

      switch (segment) {
        case 'countries':
          // Para PartnerUser, NO mostrar el country en las migajas
          // El partner es su "Home"
          break;

        case 'partners':
          // Para PartnerUser, obtener el nombre real del partner desde la URL y mostrar como home
          const urlPartnerId = this.getRouteParam('partnerId', pathSegments, i + 1);
          if (urlPartnerId) {
            const partnerName = await this.getPartnerName(urlPartnerId);
            breadcrumbs.push({
              label: partnerName, // ✅ Nombre real del partner
              url: `/admin/countries/${this.getRouteParam('countryId', pathSegments, i - 1)}/partners/${urlPartnerId}/beneficiaries`, // ✅ Home es beneficiaries
              icon: 'business',
            });
          }
          break;

        case 'beneficiaries':
          // Para PartnerUser, NO mostrar "Beneficiaries" como nivel separado
          // porque el partner ya representa su home principal
          // Check if next segment is a beneficiary ID (detail view)
          const nextSegmentPartner = pathSegments[i + 1];
          if (nextSegmentPartner && !['create', 'edit', 'forms'].includes(nextSegmentPartner)) {
            const beneficiaryName = await this.getBeneficiaryName(nextSegmentPartner);
            breadcrumbs.push({
              label: beneficiaryName,
              icon: 'person',
            });
          }
          break;

        case 'forms':
          breadcrumbs.push({
            label: 'Forms',
            url: currentPath,
            icon: 'description',
          });
          break;

        case 'create':
          this.addCreateBreadcrumb(breadcrumbs, pathSegments, i);
          break;

        case 'edit':
          this.addEditBreadcrumb(breadcrumbs, pathSegments, i);
          break;
      }
    }
  }

  private generateBasicBreadcrumbs(breadcrumbs: BreadcrumbItem[], pathSegments: string[]): void {
    let currentPath = '';
    for (const segment of pathSegments) {
      currentPath += '/' + segment;
      breadcrumbs.push({
        label: this.capitalizeFirst(segment),
        url: currentPath,
      });
    }
  }

  private addCreateBreadcrumb(
    breadcrumbs: BreadcrumbItem[],
    pathSegments: string[],
    currentIndex: number,
  ): void {
    const previousSegment = pathSegments[currentIndex - 1];
    const label = `Create ${this.capitalizeFirst(previousSegment)}`;
    breadcrumbs.push({
      label,
      icon: 'add',
    });
  }

  private addEditBreadcrumb(
    breadcrumbs: BreadcrumbItem[],
    pathSegments: string[],
    currentIndex: number,
  ): void {
    const previousSegment = pathSegments[currentIndex - 1];
    const label = `Edit ${this.capitalizeFirst(previousSegment)}`;
    breadcrumbs.push({
      label,
      icon: 'edit',
    });
  }

  private getRouteParam(
    paramName: string,
    pathSegments: string[],
    searchIndex: number,
  ): string | null {
    // Buscar el ID en la posición especificada
    if (searchIndex >= 0 && searchIndex < pathSegments.length) {
      return pathSegments[searchIndex];
    }
    return null;
  }

  public getCountryName(countryId: string): Promise<string> {
    if (this.countryCache.has(countryId)) {
      return this.countryCache.get(countryId)!;
    }

    const promise = this.countryService
      .getById(countryId)
      .toPromise()
      .then(
        (country) => country?.name || 'Country',
        () => 'Country',
      );
    this.countryCache.set(countryId, promise);
    return promise;
  }

  public getPartnerName(partnerId: string): Promise<string> {
    if (this.partnerCache.has(partnerId)) {
      return this.partnerCache.get(partnerId)!;
    }

    const promise = this.partnerService
      .getById(partnerId)
      .toPromise()
      .then(
        (partner) => partner?.name || 'Partner',
        () => 'Partner',
      );
    this.partnerCache.set(partnerId, promise);
    return promise;
  }

  public getBeneficiaryName(beneficiaryId: string): Promise<string> {
    if (this.beneficiaryCache.has(beneficiaryId)) {
      return this.beneficiaryCache.get(beneficiaryId)!;
    }

    const promise = this.beneficiaryService
      .getById(beneficiaryId)
      .toPromise()
      .then(
        (beneficiary) =>
          beneficiary ? `${beneficiary.firstName} ${beneficiary.lastName}` : 'Beneficiary',
        () => 'Beneficiary',
      );
    this.beneficiaryCache.set(beneficiaryId, promise);
    return promise;
  }

  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Pre-popula el caché con un nombre ya conocido para evitar llamadas duplicadas
   */
  public setCachedName(
    type: 'country' | 'partner' | 'beneficiary',
    id: string,
    nameOrPromise: string | Promise<string>,
  ): void {
    const promise =
      typeof nameOrPromise === 'string' ? Promise.resolve(nameOrPromise) : nameOrPromise;
    switch (type) {
      case 'country':
        this.countryCache.set(id, promise);
        break;
      case 'partner':
        this.partnerCache.set(id, promise);
        break;
      case 'beneficiary':
        this.beneficiaryCache.set(id, promise);
        break;
    }
  }

  /**
   * Invalida el caché de un country específico o todo el caché de countries
   */
  public invalidateCountryCache(countryId?: string): void {
    if (countryId) {
      this.countryCache.delete(countryId);
    } else {
      this.countryCache.clear();
    }
    this.cacheInvalidated$.next();
  }

  /**
   * Invalida el caché de un partner específico o todo el caché de partners
   */
  public invalidatePartnerCache(partnerId?: string): void {
    if (partnerId) {
      this.partnerCache.delete(partnerId);
    } else {
      this.partnerCache.clear();
    }
    this.cacheInvalidated$.next();
  }

  /**
   * Invalida el caché de un beneficiary específico o todo el caché de beneficiaries
   */
  public invalidateBeneficiaryCache(beneficiaryId?: string): void {
    if (beneficiaryId) {
      this.beneficiaryCache.delete(beneficiaryId);
    } else {
      this.beneficiaryCache.clear();
    }
    this.cacheInvalidated$.next();
  }
}
